import Location from '@r6digital/rapidstor-types/react/entities/Location';
import type { QuickSizeFilterOption, UnitFilterRule } from '@r6digital/rapidstor-types/react/entities/Journey';

import { isUnitTypePermitted } from '@/core/utils/unitFilters';

import type { UnitTypeWithImages } from './screens';

export interface FilteredUnitTypes {
    filteredUnitTypes: UnitTypeWithImages[];
    filteredGroupedUnitTypes: UnitTypeWithImages[][];
}

/** The key of the always-present "All" quick size button. */
export const ALL_QUICK_SIZE_KEY = 'all';

export const VEHICLE_QUICK_SIZE_KEY = 'vehicle';

/**
 * Whether a unit type may be shown at all.
 *
 * `hideUnitsMinAvailable` is a hard visibility rule, not a size filter: a unit
 * type with fewer than `minAvailableUnits` units left must never appear,
 * whatever the quick size filter is set to.
 */
const isVisible = (ut: UnitTypeWithImages, location: Location): boolean => {
    if (!location.rapidstor.hideUnitsMinAvailable) {
        return true;
    }
    return ut.hasComputedAvailable;
};

/**
 * The quick size buttons shown when the journey config sets none. Small,
 * medium and large come from the location thresholds; medium is strictly
 * between them.
 */
export const defaultQuickSizeOptions = (location: Location): QuickSizeFilterOption[] => {
    const small = location.rapidstor.smallMediumSizes;
    const medium = location.rapidstor.mediumLargeSizes;

    const options: QuickSizeFilterOption[] = [
        {
            key: 'small',
            label: 'standardUnitListing:small',
            filters: [{ mode: 'include', area: { max: small } }],
        },
        {
            key: 'medium',
            label: 'standardUnitListing:medium',
            filters: [
                { mode: 'include', area: { min: small, max: medium } },
                { mode: 'exclude', area: { max: small } },
                { mode: 'exclude', area: { min: medium } },
            ],
        },
        {
            key: 'large',
            label: 'standardUnitListing:large',
            filters: [{ mode: 'include', area: { min: medium } }],
        },
    ];

    if (location.rapidstor.showVehicleQuickSearch !== false) {
        options.push({
            key: VEHICLE_QUICK_SIZE_KEY,
            label: 'standardUnitListing:vehicle',
            filters: [{ mode: 'include', unitTypeNames: ['parking'], unitTypeNameMatch: 'contains' }],
        });
    }

    return options;
};

/**
 * Apply the quick size filter rules to the flat and grouped listings.
 *
 * The min-available rule is applied first; the rules only narrow what is
 * left. Groups that end up empty are dropped.
 *
 * @param unitTypes — the flat listing
 * @param groupedUnitTypes — the grouped (multilevel) listing
 * @param rules — the selected quick size button's rules; empty for "All"
 * @param location
 */
export const filterUnitTypesBySize = (
    unitTypes: UnitTypeWithImages[],
    groupedUnitTypes: UnitTypeWithImages[][],
    rules: UnitFilterRule[],
    location: Location,
): FilteredUnitTypes => {
    const keep = (ut: UnitTypeWithImages) => isVisible(ut, location)
        && isUnitTypePermitted(ut, ut.descriptor?._id, rules);

    return {
        filteredUnitTypes: unitTypes.filter(keep),
        filteredGroupedUnitTypes: groupedUnitTypes
            .map((group) => group.filter(keep))
            .filter((g) => g.length > 0),
    };
};
